import type {
  GridColDef,
  GridColumnHeaderParams,
  GridSortModel,
} from '@mui/x-data-grid'
import { DataGrid } from '@mui/x-data-grid'
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { JobRowData } from '@/api/definition'
import { fetchPaginatedJobs } from '@/api/fota'
import {
  IsOnline,
  Uid,
  DateTime,
  FotaJobStateFormatter,
} from '@/pages/utils/formatter'
import { useFotaAllFilters } from '@/hooks/useTransalationForFilter'
import { useFilters } from '@/hooks/useFilters'
import { Box } from '@mui/material'
import { CustomToolbar, CustomToolbarProps } from '@/components/DataGridToolBar'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import { FilterDrawer } from '@/components/FilterDrawer'
import { SelectedFilters } from '@/models/filters'
import { useTableUrlState } from '@/hooks/useTableUrlState'

type Props = {
  autoRefresh?: boolean
  enableFilters?: boolean
  enableSearch?: boolean
}

const ListAllJobs = ({
  autoRefresh,
  enableFilters = true,
  enableSearch = true,
}: Props): React.JSX.Element => {
  const { t } = useTranslation(['di', 'app'])
  const filterConfig = useFotaAllFilters()

  // URL state hook
  const { urlState, updateUrlState } = useTableUrlState()

  // Local filters (just for drawer state)
  const localFilterHooks = useFilters()

  // State management
  const [data, setData] = useState<JobRowData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [rowCount, setRowCount] = useState<number>(0)

  // Local search state
  const [localSearchTerm, setLocalSearchTerm] = useState(urlState.searchTerm)
  const searchDebounceTimer = useRef<NodeJS.Timeout>()

  // Refs for managing state
  const mountedRef = useRef(false)
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Use URL state for these values
  const page = urlState.pageIndex
  const pageSize = urlState.pageSize
  const searchTerm = urlState.searchTerm
  const sortModel = urlState.sortModel
  const selectedFilters = urlState.filters

  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'uid',
        headerName: t('di:device.uid'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <Uid value={params.value} />,
        width: 400,
        flex: 0,
        minWidth: 400,
        maxWidth: 400,
        resizable: false,
        sortable: true,
      },
      {
        field: 'serial',
        headerName: t('di:device.serialNr'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        filterable: false,
        minWidth: 100,
      },
      {
        field: 'label',
        headerName: t('di:device.ownerLabel'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        filterable: false,
        minWidth: 100,
      },
      {
        field: 'is_online',
        headerName: t('di:device.isOnline'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <IsOnline value={params.value} />,
        flex: 1,
        filterable: false,
        minWidth: 80,
      },
      {
        field: 'created_by',
        headerName: t('di:fota.createdBy'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        filterable: false,
        minWidth: 120,
      },
      {
        field: 'version',
        headerName: t('di:fota.firmwareVersion'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        filterable: false,
        minWidth: 80,
      },
      {
        field: 'state',
        headerName: t('di:fota.state'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <FotaJobStateFormatter state={params.value} />,
        flex: 1,
        filterable: false,
        minWidth: 80,
      },
      {
        field: 'updated_at',
        headerName: t('di:fota.lastChange'),
        renderHeader: renderWrappedHeader,
        renderCell: (params) => <DateTime value={params.value} />,
        flex: 1,
        filterable: false,
        minWidth: 100,
      },
    ],
    [t],
  )

  const loadData = useCallback(
    async (
      currentPage: number,
      currentPageSize: number,
      currentSortModel: GridSortModel,
      currentSearchTerm: string,
      currentSelectedFilters: SelectedFilters,
      isAutoRefresh = false,
    ) => {
      if (!mountedRef.current) return

      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      if (!isAutoRefresh) setLoading(true)

      try {
        const hasSortModel = currentSortModel.length > 0
        let sortParam = 'updated_at'
        if (hasSortModel) {
          sortParam =
            currentSortModel[0].field === 'state'
              ? 'State'
              : currentSortModel[0].field
        }
        const sortDirection = hasSortModel ? currentSortModel[0].sort : 'desc'

        const statesToQuery = enableFilters
          ? Object.values(currentSelectedFilters).flatMap((g) => Object.keys(g))
          : []

        const { data: jobData, totalRows } = await fetchPaginatedJobs({
          page: currentPage,
          pageSize: currentPageSize,
          sortBy: sortParam,
          sortDir: sortDirection,
          searchTerm: currentSearchTerm,
          state: statesToQuery,
        })

        const canUpdate =
          mountedRef.current && !abortControllerRef.current?.signal.aborted

        if (canUpdate) {
          setData(jobData)
          setRowCount(totalRows)
        }
      } catch (error) {
        const canLogError =
          mountedRef.current && !abortControllerRef.current?.signal.aborted
        if (canLogError) {
          console.error('Error fetching data:', error)
        }
      } finally {
        if (mountedRef.current && !isAutoRefresh) {
          setLoading(false)
        }
      }
    },
    [enableFilters],
  )

  // Single effect that watches URL state
  useEffect(() => {
    mountedRef.current = true
    loadData(page, pageSize, sortModel, searchTerm, selectedFilters)

    return () => {
      mountedRef.current = false
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [page, pageSize, sortModel, searchTerm, selectedFilters, loadData])

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && mountedRef.current) {
      autoRefreshIntervalRef.current = setInterval(() => {
        if (mountedRef.current) {
          loadData(page, pageSize, sortModel, searchTerm, selectedFilters, true)
        }
      }, 30000)

      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current)
          autoRefreshIntervalRef.current = null
        }
      }
    }
  }, [
    autoRefresh,
    page,
    pageSize,
    sortModel,
    searchTerm,
    selectedFilters,
    loadData,
  ])

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }
    }
  }, [])

  // Event handlers
  const handlePaginationModelChange = useCallback(
    (params: { page: number; pageSize: number }) => {
      updateUrlState({
        pageIndex: params.page,
        pageSize: params.pageSize,
      })
    },
    [updateUrlState],
  )

  // Debounced search handler
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value

      // Update local state immediately
      setLocalSearchTerm(newSearchTerm)

      // Clear existing timer
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current)
      }

      // Debounce the URL state update (300ms)
      searchDebounceTimer.current = setTimeout(() => {
        updateUrlState({
          pageIndex: 0,
          searchTerm: newSearchTerm,
        })
      }, 300)
    },
    [updateUrlState],
  )

  const handleFilterChange = useCallback(
    (
      groupId: string,
      optionKey: string,
      optionLabel: string,
      isSelected: boolean,
    ) => {
      const newFilters = { ...selectedFilters }

      if (!newFilters[groupId]) {
        newFilters[groupId] = {}
      }

      if (isSelected) {
        newFilters[groupId][optionKey] = optionLabel
      } else {
        delete newFilters[groupId][optionKey]
        if (Object.keys(newFilters[groupId]).length === 0) {
          delete newFilters[groupId]
        }
      }

      updateUrlState({
        pageIndex: 0,
        filters: newFilters,
      })
    },
    [selectedFilters, updateUrlState],
  )

  const handleClearAllFilters = useCallback(() => {
    updateUrlState({
      pageIndex: 0,
      filters: {},
    })
  }, [updateUrlState])

  const handleRemoveFilter = useCallback(
    (groupId: string, optionKey: string) => {
      const currentLabel = selectedFilters[groupId]?.[optionKey] || ''
      handleFilterChange(groupId, optionKey, currentLabel, false)
    },
    [selectedFilters, handleFilterChange],
  )

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      updateUrlState({
        pageIndex: 0,
        sortModel: model,
      })
    },
    [updateUrlState],
  )

  const shouldShowCustomToolbar = enableFilters || enableSearch

  const activeFilterCount = enableFilters
    ? Object.values(selectedFilters).reduce(
        (count, group) => count + Object.keys(group).length,
        0,
      )
    : 0

  return (
    <>
      <Box
        sx={{
          borderRadius: '4px',
          overflow: 'auto',
          width: '100%',
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            lineHeight: '1.5',
          },
        }}
      >
        <DataGrid
          rowHeight={50}
          rows={data}
          getRowId={(row) => row.id}
          columns={columns}
          autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
          disableVirtualization
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          loading={loading}
          rowCount={rowCount}
          paginationModel={{ page: page, pageSize: pageSize }}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          slots={
            shouldShowCustomToolbar ? { toolbar: CustomToolbar } : undefined
          }
          slotProps={{
            toolbar: shouldShowCustomToolbar
              ? ({
                  onFilterClick: enableFilters
                    ? localFilterHooks.openDrawer
                    : undefined,
                  activeFilterCount: activeFilterCount,
                  enableFilters: enableFilters,
                  enableSearch: enableSearch,
                  selectedFilters: selectedFilters,
                  filterConfig: filterConfig,
                  onRemoveFilter: handleRemoveFilter,
                  searchValue: localSearchTerm,
                  onSearchChange: handleSearchChange,
                } as CustomToolbarProps)
              : undefined,
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              ActionsComponent: TablePaginationActions,
            },
          }}
          localeText={{
            noRowsLabel: 'No jobs found',
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {enableFilters && filterConfig && (
        <FilterDrawer
          open={localFilterHooks.isDrawerOpen}
          onClose={localFilterHooks.closeDrawer}
          filterConfig={filterConfig}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAllFilters}
        />
      )}
    </>
  )
}

export default ListAllJobs
